import type { GetServerSideProps } from 'next';
import Head from 'next/head';
import Script from 'next/script';
import React, { useEffect, useState } from 'react';
import UAParser from 'ua-parser-js';

import AuthLayout from '@/components/Auth/AuthLayout';
import GetAppModalContent from '@/components/GetAppComp/GetAppModalContent';

import { getGlobalCountry } from '@/atoms/countryState';

interface Props {
  androidRating: string | null;
  androidCount: number | null;
  iosRating: string | null;
  iosCount: number | null;
}

const GetOurApp: React.FC<Props> = ({ iosCount, iosRating }) => {
  const [androidRedirectUrl, setAndroidRedirectUrl] = useState<string | null>(
    null,
  );
  const [hasRedirected, setHasRedirected] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined' || hasRedirected) return;

    const parser = new UAParser();
    const result = parser.getResult();
    const deviceType = result.device.type;
    const osName = result.os.name;

    const urlParams = new URLSearchParams(window.location.search);

    const defaultParams: Record<string, string> = {
      utm_source: 'zebralearn',
      utm_medium: 'website',
      utm_campaign: 'download_app',
    };

    const finalParams = new URLSearchParams();

    urlParams.forEach((value, key) => {
      finalParams.set(key, value);
    });

    Object.entries(defaultParams).forEach(([key, value]) => {
      if (!finalParams.has(key)) {
        finalParams.set(key, value);
      }
    });

    const encodedReferrer = encodeURIComponent(finalParams.toString());
    const androidUrl = `https://play.google.com/store/apps/details?id=com.zebralearn.reader&referrer=${encodedReferrer}`;
    const iosUrl =
      'https://apps.apple.com/in/app/zebralearn/id6477746268?utm_source=zebralearn_website';

    setAndroidRedirectUrl(androidUrl);

    if (deviceType === 'mobile' || deviceType === 'tablet') {
      if (osName === 'Android') {
        setHasRedirected(true);
        window.location.href = androidUrl;
      } else if (osName === 'iOS') {
        setHasRedirected(true);
        window.location.href = iosUrl;
      }
    }
  }, [hasRedirected]);

  const fallbackRating = iosRating || '4.8';
  const fallbackCount = iosCount || 1000;

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'MobileApplication',
    name: 'ZebraLearn',
    operatingSystem: 'ANDROID, iOS',
    applicationCategory: 'EducationApplication',
    applicationSubCategory: 'Exam Preparation',
    url: 'https://zebralearn.com/get-our-app',
    image: 'https://zebralearn.com/logo.png',
    logo: {
      '@type': 'ImageObject',
      url: 'https://zebralearn.com/logo.png',
    },
    description:
      'Discover the best books and courses on finance, career growth, and more.',
    publisher: {
      '@type': 'Organization',
      name: 'ZebraLearn',
      logo: {
        '@type': 'ImageObject',
        url: 'https://zebralearn.com/logo.png',
      },
    },
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: getGlobalCountry().countryCode || 'INR',
      availability: 'https://schema.org/InStock',
    },
    inAppPurchase: true,
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: fallbackRating,
      reviewCount: fallbackCount,
      bestRating: '5',
    },
    downloadUrl: [
      'https://play.google.com/store/apps/details?id=com.zebralearn.reader',
      'https://apps.apple.com/in/app/zebralearn/id6477746268',
    ],
  };

  return (
    <>
      <Head>
        <title>Download the ZebraLearn App</title>
        <meta
          name='description'
          content='Download ZebraLearn for Android and iOS. Access expert books, courses, and exam-prep materials. Free app with in-app purchases.'
        />
        <link rel='canonical' href='https://zebralearn.com/get-our-app' />
      </Head>

      <Script
        id='mobile-app-schema'
        type='application/ld+json'
        strategy='afterInteractive'
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />

      <AuthLayout isModal={false}>
        <GetAppModalContent androidRedirectUrl={androidRedirectUrl} />
      </AuthLayout>
    </>
  );
};

export const getServerSideProps: GetServerSideProps = async () => {
  try {
    const appleRes = await fetch(
      'https://itunes.apple.com/in/lookup?bundleId=com.zebralearn.reader',
    );
    const status = appleRes.status;
    const contentType = appleRes.headers.get('content-type') || '';
    const rawText = await appleRes.text();

    let iosRating = null;
    let iosCount = null;

    const isJsonLike =
      contentType.includes('application/json') ||
      contentType.includes('text/javascript');

    if (status === 200 && isJsonLike) {
      try {
        const appleJson = JSON.parse(rawText);
        const appleData = appleJson.results?.[0];
        iosRating = appleData?.averageUserRating?.toFixed(1) ?? null;
        iosCount = appleData?.userRatingCount ?? null;
      } catch (err) {
        console.error('[SSR] Apple JSON parse error:', err);
      }
    } else {
      console.warn('[SSR] Apple API returned non-JSON-ish content');
    }

    return {
      props: {
        // androidRating: googleData.score?.toFixed(1) ?? null,
        // androidCount: googleData.reviews ?? null,
        iosRating,
        iosCount,
      },
    };
  } catch (error) {
    console.error('[SSR] Error fetching ratings:', error);
    return {
      props: {
        androidRating: null,
        androidCount: null,
        iosRating: null,
        iosCount: null,
      },
    };
  }
};

export default GetOurApp;
